import React, { useState, useMemo } from "react";
import InputField from "../../../../components/InputField";
import {
  VariantBigBlockWithoutImageProps,
  VariantDataProps,
} from "../../../../types";
import { v4 as uuidv4 } from "uuid";
import { validatePrice } from "../../../../utils/validations";
import Button from "../../../../components/Button";
import MultipleInputFieldTwo from "../../components/MultipleInputFieldTwo";
import Modal from "../../../../components/common/Modal";
import ImageUpload from "../../../../components/ImageUpload";
import { removeExtraSpacesfromObject } from "../../../../utils/helper";

type BigBlockWithoutImgPopupProps = {
  handleClose: () => void;
  setVariantData: React.Dispatch<React.SetStateAction<VariantDataProps>>;
  data?: VariantBigBlockWithoutImageProps;
  task?: any;
  setVariantPopupError?: any;
};

// BIG_BLOCK_WITHOUT_IMAGE Option Schema
// {
//   "name": "Option #1",
//   "price": 20,
//   "product_image": "products/bd0b7d82-24b4-46cc-a19b-57cc12a41368.jpg",
//   "code": "A354",
//   "properties": {
//     "key": "value"
//   }
// }
const initState = {
  name: "",
  price: 0,
  product_image: "",
  code: null,
  properties: {},
  type: "BIG_BLOCK_WITHOUT_IMAGE",
};
export default function BigBlockWithoutImgPopup({
  handleClose,
  setVariantData,
  data = initState,
  task,
  setVariantPopupError,
}: BigBlockWithoutImgPopupProps) {
  const [bigBlockWithoutImgData, setBigBlockWithoutImgData] =
    useState<VariantBigBlockWithoutImageProps>(structuredClone(data));

  const [errorMessage, setErrorMessage] = useState<any>({
    code: [],
    name: [],
    price: [],
    product_image: [],
  });

  const handleSubmitData = () => {
    if (!bigBlockWithoutImgData?.name) {
      if (!bigBlockWithoutImgData?.name) {
        setErrorMessage((e: any) => ({
          ...e,
          name: ["Please enter the name"],
        }));
      }

      return;
    }

    if (bigBlockWithoutImgData?.properties) {
      const entries = Object.entries(bigBlockWithoutImgData?.properties).filter(
        ([key, value]) => !!key
      );
      if (entries.length) {
        bigBlockWithoutImgData.properties = Object.fromEntries(entries);
      }
    }

    const clone: any = removeExtraSpacesfromObject(structuredClone(bigBlockWithoutImgData));

    if (
      clone.price === "" ||
      (clone.price && +clone.price == 0) ||
      !clone.price
    ) {
      clone.price = "0";
    }
    if (clone.price) {
      clone.price = parseFloat(clone.price);
    }

    delete clone.type;
    if (data.id && task !== "copy") {
      // Edit Option Functionality
      setVariantData((prev) => ({
        ...prev,
        options: prev.options.map((el) => (el.id === data.id ? clone : el)),
      }));
    } else {
      // Add Option Functionality
      const copyID = data.id;
      const newData = { ...clone, id: uuidv4(), added: true };
      setVariantData((prev) => {
        if (task === "copy") {
          const findIndex = prev.options.findIndex(
            (option) => option?.id === copyID
          );
          const updatedOptions = [...prev.options];
          updatedOptions.splice(findIndex + 1, 0, newData);
          return {
            ...prev,
            options: updatedOptions,
          };
        } else {
          const deleteIndex = prev.options.findIndex(
            (option) => option?.to_delete
          );

          if (deleteIndex === -1) {
            return {
              ...prev,
              options: [...prev.options, newData],
            };
          }

          const updatedOptions = [...prev.options];
          updatedOptions.splice(deleteIndex, 0, newData);
          return {
            ...prev,
            options: updatedOptions,
          };
        }
      });
    }
    setBigBlockWithoutImgData(initState);
    setVariantPopupError((prev: any) => ({ ...prev, options: [] }));
    handleClose();
  };

  const productImageList = useMemo(
    () =>
      bigBlockWithoutImgData.product_image
        ? [bigBlockWithoutImgData.product_image]
        : [],
    [bigBlockWithoutImgData.product_image]
  );

  return (
    <Modal
      allowScroll
      header={
        task === "copy"
          ? "Add an Option"
          : data.name
          ? "Edit Option"
          : "Add an Option"
      }
      footer={
        <Button
          label={
            task === "copy"
              ? "Add Option"
              : bigBlockWithoutImgData.id
              ? "Update"
              : "Add Option"
          }
          variant="secondary"
          onClick={handleSubmitData}
        />
      }
      handleCancel={handleClose}
    >
      <div className="w-full h-full flex flex-col gap-3">
        {/* code */}
        <InputField
          onChange={(e) => {
            setBigBlockWithoutImgData((prevData) => ({
              ...prevData,
              code: e?.toUpperCase(), // Set code to null if empty, otherwise to uppercase value
            }));
            setErrorMessage((e: any) => ({ ...e, code: [] }));
          }}
          value={bigBlockWithoutImgData.code}
          placeholder="Code"
          label="Code:"
          hint={errorMessage?.code}
          maxLength={10}
        />
        {/* name */}
        <InputField
          onChange={(e) => {
            setBigBlockWithoutImgData((prevData) => ({
              ...prevData,
              name: e,
            }));
            setErrorMessage((e: any) => ({ ...e, name: [] }));
          }}
          value={bigBlockWithoutImgData.name}
          placeholder="Name"
          label="Name"
          hint={errorMessage?.name}
          required
        />

        {/* price */}
        <InputField
          onChange={(e: any) => {
            if (e > 99999) {
              setErrorMessage((prevError: any) => ({
                ...prevError,
                price: ["Price should be less than or equal to 99999."],
              }));
            } else {
              setBigBlockWithoutImgData((prevData) => ({
                ...prevData,
                price: e,
              }));
              setErrorMessage((prevError: any) => ({
                ...prevError,
                price: [],
              }));
            }
          }}
          value={bigBlockWithoutImgData.price}
          placeholder="Price"
          label="Price:"
          type="price"
          validate={validatePrice}
          hint={errorMessage?.price}
        />

        {/*properties */}
        <MultipleInputFieldTwo
          properties={bigBlockWithoutImgData?.properties}
          setFormData={setBigBlockWithoutImgData}
        />

        {/* product_image */}
        <ImageUpload
          label="Upload Product Image:"
          imageList={productImageList}
          setter={(value) => {
            setBigBlockWithoutImgData((prev) => ({
              ...prev,
              product_image: value as string,
            }));
          }}
          kind="PRODUCT"
          errors={errorMessage?.product_image}
          removeError={() =>
            setErrorMessage((e: any) => ({ ...e, product_image: [] }))
          }
        />
      </div>
    </Modal>
  );
}
